import { CircleCheck, TriangleAlert } from "lucide-react";

import type { SignatureRecipientPreparation } from "@/api/signature/recipient";
import { DocumentosLabels } from "@/lib/labels/documentos";
import { cn } from "@/lib/utils";

export function SendTermsRecipient({
	personId,
	recipient,
}: {
	personId: number;
	recipient: SignatureRecipientPreparation;
}) {
	const ready = recipient.blockers.length === 0;
	const facts = [
		{ label: DocumentosLabels.preparation.name, value: recipient.name },
		{
			label: DocumentosLabels.preparation.cpf,
			value: recipient.cpf ?? DocumentosLabels.preparation.missingValue,
		},
		{
			label: DocumentosLabels.preparation.phone,
			value: recipient.phoneDisplay ?? DocumentosLabels.preparation.missingValue,
		},
	];

	return (
		<section data-component="signature-recipient" data-ready={ready} className="surface-panel p-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex items-center gap-2">
					{ready && <CircleCheck className="size-4 text-whatsapp" />}
					{!ready && <TriangleAlert className="size-4 text-destructive" />}

					<div>
						<h3 className="text-sm font-semibold text-foreground">
							{DocumentosLabels.preparation.recipient}
						</h3>
						<p className={cn("text-xs", ready && "text-whatsapp", !ready && "text-destructive")}>
							{ready && DocumentosLabels.preparation.recipientReady}
							{!ready && DocumentosLabels.preparation.recipientBlocked}
						</p>
					</div>
				</div>

				{!ready && (
					<a
						href={`/pessoa/${personId}?tab=cadastro`}
						className="rounded-md text-xs font-medium text-primary hover:text-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					>
						{DocumentosLabels.preparation.openRegistration}
					</a>
				)}
			</div>

			<div className="mt-4 grid gap-3 sm:grid-cols-3">
				{facts.map((fact) => (
					<div key={fact.label}>
						<p className="text-xs text-muted-foreground">{fact.label}</p>
						<p className="mt-0.5 text-sm font-medium text-foreground">{fact.value}</p>
					</div>
				))}
			</div>

			{!ready && (
				<ul className="mt-3 flex flex-wrap gap-2">
					{recipient.blockers.map((code) => (
						<li
							key={code}
							className="rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1 text-xs text-destructive"
						>
							{DocumentosLabels.preparation.recipientIssue(code)}
						</li>
					))}
				</ul>
			)}
		</section>
	);
}
